import { OrderedSet } from 'immutable';
import { CommandPayloadType, LexicalCommand, LexicalEditor } from 'lexical';
import { convertBrandFontRefToFontFamily } from 'modules/BrandDefinition';
import { usePropsUpdater } from 'modules/Lexical/hooks/usePropsUpdater';
import { LexicalAbbreviationValues, ABBREVIATION_CHANGE } from 'modules/Lexical/Plugins/AbbreviationPlugin';
import { $selectAbbreviation } from 'modules/Lexical/Plugins/AbbreviationPlugin/utils';
import { BASE_PROPS_COMMAND, BaseProps } from 'modules/Lexical/Plugins/BasePropsPlugin';
import { CustomListOnChangeProps, BULLET_COLOR_COMMAND } from 'modules/Lexical/Plugins/CustomListPlugin';
import { FONT_COMMAND, FontProps } from 'modules/Lexical/Plugins/FontPlugin';
import { LINK_COMMAND, LinkProps } from 'modules/Lexical/Plugins/LinkNodePlugin';
import { TEXT_BLOCK_COMMAND, TextBlockProps } from 'modules/Lexical/Plugins/TextBlockPlugin';
import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import * as Constants from 'const';
import * as Models from 'models';
import * as editorUtils from 'utils/editor';
import { TextEditorHook, TextEditorSetters, TextEditorProps } from '../../../components/ArtboardAssets/Text/hooks/TextEditorHook';

export type LexicalOnChange = {
  abbreviations: (values: LexicalAbbreviationValues) => void;
  baseProps: (values: BaseProps) => void;
  textBlock: (values: TextBlockProps) => void;
  bulletToggle: (values: CustomListOnChangeProps) => void;
  font: (values: FontProps) => void;
  link: (values: LinkProps) => void;
  textContent: (value: string) => void;
};

type LexicalTextEditorHook = TextEditorHook & {
  ref: RefObject<LexicalEditor>;
  onChange: LexicalOnChange;
};

export function useLexicalTextEditor(
  setBrandStyleChanged: (val: boolean) => void,
): LexicalTextEditorHook {
  const setBrandStyleChangedRef = useRef(setBrandStyleChanged);
  setBrandStyleChangedRef.current = setBrandStyleChanged;

  const ref = useRef<LexicalEditor>(null);

  const { props, updateProps } = usePropsUpdater<TextEditorProps>({
    abbreviationId: undefined,
    abbreviationTerm: undefined,
    blockLineHeight: undefined,
    blockType: undefined,
    bulletColor: undefined,
    fontColor: undefined,
    fontFamily: undefined,
    fontSize: undefined,
    inlineStyle: OrderedSet<Constants.InlineStyle>([]),
    link: undefined,
    linkApplicable: false,
    scriptStyle: undefined,
    textNoWrap: false,
  });

  // IN-PROGRESS: get initial value from props
  const [textContent, setTextContent] = useState('');

  const dispatchEditorCommand = useCallback(<T extends LexicalCommand<unknown>>(
    type: T,
    payload: CommandPayloadType<T>,
    changeBrandStyle: boolean,
  ): void => {
    if (!ref.current) {
      return;
    }
    if (changeBrandStyle) {
      setBrandStyleChangedRef.current(true);
    }
    ref.current.dispatchCommand(type, payload);
  }, []);

  const setters = useMemo((): TextEditorSetters => ({
    abbreviationId: (id: string | undefined) => dispatchEditorCommand(ABBREVIATION_CHANGE, id, false),
    blockLineHeight: value => dispatchEditorCommand(TEXT_BLOCK_COMMAND.BLOCK_LINE_HEIGHT, value, true),
    blockType: type => dispatchEditorCommand(TEXT_BLOCK_COMMAND.BLOCK_TYPE, type, true),
    bulletColor: color => dispatchEditorCommand(BULLET_COLOR_COMMAND, color, true),
    fontColor: brandColor => dispatchEditorCommand(FONT_COMMAND.BRAND_COLOR, brandColor, true),
    fontFamily: (
      brandFont: Models.BrandFontMap,
      characterStyle: Models.CharacterStyleMap,
    ) => dispatchEditorCommand(FONT_COMMAND.BRAND_FONT, { brandFont, characterStyle }, true),
    fontSize: value => dispatchEditorCommand(FONT_COMMAND.SIZE, value, true),
    inlineStyle: style => dispatchEditorCommand(FONT_COMMAND.INLINE_STYLE, style, false),
    link: (value: string | undefined) => dispatchEditorCommand(LINK_COMMAND, value, false),
    scriptStyle: value => dispatchEditorCommand(BASE_PROPS_COMMAND.SCRIPT_STYLE, value, false),
    textNoWrap: () => dispatchEditorCommand(BASE_PROPS_COMMAND.TEXT_NOWRAP, undefined, false),
  }), [dispatchEditorCommand]);

  const onChange: LexicalOnChange = useMemo(() => ({
    abbreviations: values => updateProps({
      abbreviationId: values.abbreviationId,
      abbreviationTerm: values.abbreviationTerm,
    }),
    baseProps: (values: BaseProps) => updateProps({
      scriptStyle: values.scriptStyle,
      textNoWrap: values.textNoWrap,
    }),
    textBlock: (values: TextBlockProps) => updateProps(prev => ({
      blockLineHeight: values.blockLineHeight,
      blockType: values.blockType ?? (prev.blockType ?? Constants.TextHorizontalAlignmentType.LEFT),
    })),
    bulletToggle: values => updateProps(prev => ({
      blockType: values
        ? Constants.TextHorizontalAlignmentType.UNORDERED_LIST
        : (prev.blockType ?? Constants.TextHorizontalAlignmentType.LEFT),
      bulletColor: values?.color,
    })),
    font: values => updateProps({
      fontColor: values.brandColor,
      fontFamily: values.brandFont && convertBrandFontRefToFontFamily(values.brandFont),
      fontSize: values.size,
      inlineStyle: values.inlineStyle,
    }),
    link: values => updateProps({
      link: values.link,
      linkApplicable: values.linkApplicable,
    }),
    textContent: value => setTextContent(value),
  }), [updateProps]);

  const applyBrandStyleValues = useCallback(() => { }, []);

  const returnFocusToEditor = useCallback(() => {
    // https://github.com/facebook/lexical/issues/4460
    ref.current?._rootElement?.focus();
  }, []);

  // CELL ACTION EXECUTORS

  const setCursorOnAbbreviation = (abbreviationId: string, abbreviationNumber: number | undefined): boolean => {
    let results = false;
    ref.current?.update(() => {
      results = $selectAbbreviation(abbreviationId, abbreviationNumber);
    }, { discrete: true });

    return results;
  };

  const applySelection = (): void => {
    // lexical abbreviation works without this action executor
    // so we decide to skip its implementation
  };

  const flushDataToSave = useCallback(() => {
    const rawContent = JSON.stringify(ref.current?.getEditorState());

    return { text: '', rawContent, operations: [] };
  }, []);

  return {
    // TextEditorHook --------------
    hasChanges: true, // IN-PROGRESS should be implemented
    hasTextContent: Boolean(textContent.length),
    hasCustomToken: editorUtils.hasToken(textContent),
    props,
    setters,
    applyBrandStyleValues,
    returnFocusToEditor,
    flushDataToSave,
    // TextEditorHook - CELL ACTION EXECUTORS
    setCursorOnAbbreviation,
    applySelection,
    // custom --------------
    ref,
    onChange,
  };
}
